import React from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { OUTILS_CATALOG, canAccessTool } from './OutilViewerPage';
import { useAuth } from '../context/AuthContext';
import { Smartphone, Monitor, ExternalLink, Layers, AlertOctagon, Gavel, ClipboardCheck, FileCheck2, Crosshair, CheckCircle2, Route, GraduationCap, UserCheck, Eye, ChevronRight, Film } from 'lucide-react';

const ICON_BY_SLUG = {
  'analyse-video-giratoire': Film,
  'cours-stop-cedez': Crosshair,
  'cours-intersections-priorite': Route,
  'cours-en-salle': Monitor,
  'cours-voiture-mobile': Smartphone,
  'rdv-1-2-aac-mobile': Route,
  'infractions-sanctions-2026': Gavel,
  'sanctions-2026-fiche-rapide': AlertOctagon,
  'examen-blanc-grille-officielle': ClipboardCheck,
  'examen-blanc-simulation': FileCheck2,
  'bilan-c1': CheckCircle2,
  'bilan-c2': CheckCircle2,
  'bilan-c3': CheckCircle2,
  'bilan-c4': CheckCircle2,
  'bilan-intersections': Crosshair,
};

const LONG_DESC = {
  'analyse-video-giratoire': "Deux vidéos réelles de giratoire hors agglo décodées seconde par seconde : timeline à timecodes, pièges PL + moto, courbe d'allure, et grille de révision à cocher (seuil 8/11) pour valider l'autonomie.",
  'cours-stop-cedez': "Le stop et le cédez-le-passage décodés par un moniteur ECSR : schéma d'intersection animé en 4 étapes, comparatif face-à-face, QCM de 6 questions par règle, pièges éliminatoires d'examen et grille de révision à cocher.",
  'cours-intersections-priorite': "La priorité à droite et la route prioritaire décodées : carrefour animé en 4 étapes, règle par défaut R415-5, panneaux AB1/AB2a/AB6a-b, QCM de 6 questions par régime, pièges d'examen et grille de révision à cocher.",
  'cours-en-salle': "Support de cours en salle pour enseignant ECSR : format projection, idéal pour leçons collectives en auto-école.",
  'cours-voiture-mobile': "Application mobile pour l'élève : revue des compétences pendant la formation pratique, 38 leçons REMC chartrées codevoiture.fr.",
  'rdv-1-2-aac-mobile': "Support mobile fusionné des Rendez-vous pédagogiques 1 et 2 de l'Apprentissage Anticipé de la Conduite (AAC). Idéal pour l'accompagnement post-formation initiale.",
  'infractions-sanctions-2026': "Plateforme interactive complète des infractions et sanctions routières 2026. Barème à jour, recherche par mot-clé, retrait de points, amendes.",
  'sanctions-2026-fiche-rapide': "Fiche synthétique des sanctions routières 2026 : amendes, points retirés, suspensions. Outil mémo pour l'élève et le moniteur.",
  'examen-blanc-grille-officielle': "Grille d'évaluation type permis B reproduisant les compétences et critères officiels de l'examen pratique. Idéal pour simuler une évaluation finale.",
  'examen-blanc-simulation': "Version courte d'examen blanc pour entraîner l'élève aux conditions d'évaluation de l'épreuve pratique du permis B.",
  'bilan-c1': "Bilan synthétique de fin de compétence 1 du REMC : maîtrise des commandes, direction, allure, regard, prise d'information.",
  'bilan-c2': "Bilan synthétique de fin de compétence 2 du REMC : circulation dans des situations de difficulté croissante, partage de la route.",
  'bilan-c3': "Bilan synthétique de fin de compétence 3 du REMC : conduite par conditions difficiles, autoroute, voies rapides, nuit, partage avec les autres usagers.",
  'bilan-c4': "Bilan synthétique de fin de compétence 4 du REMC : autonomie, conduite éco-responsable, gestion du risque et des situations imprévues.",
  'bilan-intersections': "Bilan ciblé sur la gestion des intersections : priorités, lecture du carrefour, prise de décision, communication. Outil de diagnostic pédagogique.",
};

const GROUP_ORDER = [
  { name: 'Analyses vidéo & cours interactifs', slugs: ['analyse-video-giratoire', 'cours-stop-cedez', 'cours-intersections-priorite'] },
  { name: 'Livrets & supports de cours', slugs: ['cours-en-salle', 'cours-voiture-mobile', 'rdv-1-2-aac-mobile'] },
  { name: 'Infractions & sanctions 2026', slugs: ['infractions-sanctions-2026', 'sanctions-2026-fiche-rapide'] },
  { name: 'Examens blancs & évaluations', slugs: ['examen-blanc-grille-officielle', 'examen-blanc-simulation'] },
  { name: 'Bilans de compétences C1 → C4', slugs: ['bilan-c1', 'bilan-c2', 'bilan-c3', 'bilan-c4', 'bilan-intersections'] },
];

const ROLE_LABEL = {
  eleve: { l: 'Élève', icon: GraduationCap, color: 'text-emerald-300 border-emerald-700/40 bg-emerald-600/10' },
  enseignant: { l: 'Enseignant ECSR', icon: UserCheck, color: 'text-amber-300 border-amber-700/40 bg-amber-600/10' },
};

function ToolCard({ slug, allowed }) {
  const tool = OUTILS_CATALOG[slug];
  const Icon = ICON_BY_SLUG[slug] || Layers;
  if (!tool) return null;
  // Redirection native si disponible
  const linkTarget = tool.native_path || `/outil/${slug}`;
  const isNative = !!tool.native_path;
  return (
    <Link
      data-testid={`cours-interactif-card-${slug}`}
      to={linkTarget}
      className={`group rounded-2xl border bg-[#0d0d0e] p-6 hover:border-red-700/40 transition flex flex-col card-lift ${allowed ? 'border-white/10' : 'border-white/5 opacity-55 hover:opacity-90'}`}
    >
      <div className="flex items-start justify-between mb-5">
        <div className="w-12 h-12 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400">
          <Icon size={22} />
        </div>
        <div className="flex flex-col items-end gap-1">
          {isNative && <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[9.5px] font-bold tracking-wider">CHARTÉ</span>}
          <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10.5px] font-bold tracking-wider">{tool.badge}</span>
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-2">{tool.audienceLabel}</div>
      <h3 className="font-serif-editorial text-white text-[22px] leading-tight">{tool.title}</h3>
      <div className="text-red-400 text-[12.5px] font-mono-spec mt-1">{tool.subtitle}</div>
      <p className="text-white/60 text-[13.5px] mt-3 leading-relaxed flex-1">{LONG_DESC[slug]}</p>

      {/* Profil tags */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {tool.audience.map((r) => {
          const meta = ROLE_LABEL[r];
          if (!meta) return null;
          const I = meta.icon;
          return (
            <span key={r} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>
              <I size={10} /> {meta.l}
            </span>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-white/5 inline-flex items-center justify-between">
        <span className="text-red-400 font-semibold text-[13px] inline-flex items-center gap-1.5">
          <Layers size={13} /> {allowed ? "Ouvrir l'outil" : 'Profil requis'}
        </span>
        <ExternalLink size={12} className="text-white/30 group-hover:text-white/70 transition" />
      </div>
    </Link>
  );
}

export default function CoursInteractifsPage() {
  const { user } = useAuth();
  const userRole = user?.role || 'eleve';
  const isAdmin = user?.is_admin || false;

  // Construire les groupes filtrés selon le rôle
  const filteredGroups = GROUP_ORDER.map((g) => ({
    ...g,
    items: g.slugs
      .map((s) => ({ slug: s, allowed: canAccessTool(OUTILS_CATALOG[s], userRole, isAdmin) }))
      .filter((it) => it.allowed), // STRICT : on cache les outils non autorisés
  })).filter((g) => g.items.length > 0);

  const totalVisible = filteredGroups.reduce((acc, g) => acc + g.items.length, 0);
  const totalCatalog = Object.keys(OUTILS_CATALOG).length;
  const profileLabel = isAdmin ? 'Administrateur' : (ROLE_LABEL[userRole]?.l || 'Visiteur');

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12" data-testid="cours-interactifs-page">
        <SectionTitle
          kicker={`Cours interactifs · Profil : ${profileLabel}`}
          title={`${totalVisible} outil${totalVisible > 1 ? 's' : ''} adapté${totalVisible > 1 ? 's' : ''} à ton profil`}
          sub="Chaque outil est filtré selon ton rôle (Élève ou Enseignant ECSR). Connecte-toi avec ton profil pour accéder à tes ressources."
        />

        {/* Démos interactives — accès rapide */}
        <Link
          to="/demos-interactives"
          data-testid="cours-demos-cta"
          className="mb-8 group block rounded-2xl border border-red-700/40 bg-gradient-to-r from-red-950/40 via-[#150a0a] to-[#0d0d0e] p-4 sm:p-5 hover:border-red-500 transition relative overflow-hidden"
        >
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-red-900/40">
              <Eye size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 mb-1">
                <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-red-300 font-semibold">8 démos pédagogiques</span>
              </div>
              <div className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight">Tache aveugle, angles morts, panneaux flash, vision sous alcool…</div>
              <div className="text-white/60 text-[12.5px] sm:text-[13px] mt-1">Démonstrations interactives à projeter en salle ou à tester en autonomie.</div>
            </div>
            <div className="text-red-400 group-hover:translate-x-1 transition shrink-0"><ChevronRight size={20} /></div>
          </div>
        </Link>

        {/* Profil banner */}
        {!user && (
          <div className="mb-8 rounded-2xl border border-amber-700/30 bg-amber-950/15 p-4 flex items-start gap-3" data-testid="visitor-banner">
            <GraduationCap className="text-amber-400 shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <div className="text-amber-200 font-semibold text-[13.5px]">Tu navigues en visiteur — vue Élève par défaut</div>
              <div className="text-white/60 text-[12.5px] mt-0.5">Connecte-toi pour accéder aux outils Enseignant ECSR.</div>
            </div>
          </div>
        )}
        {user && totalVisible < totalCatalog && (
          <div className="mb-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 flex items-center gap-3 text-[12.5px] text-white/60" data-testid="role-filter-info">
            <Layers size={14} className="text-red-400 shrink-0" />
            <span>{totalCatalog - totalVisible} outil{totalCatalog - totalVisible > 1 ? 's' : ''} masqué{totalCatalog - totalVisible > 1 ? 's' : ''} (réservé{totalCatalog - totalVisible > 1 ? 's' : ''} à d'autres profils).</span>
          </div>
        )}

        <div className="space-y-14">
          {filteredGroups.map((g) => (
            <div key={g.name}>
              <div className="flex items-baseline justify-between mb-4 sm:mb-5 pb-3 border-b border-white/5">
                <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">{g.name}</h3>
                <span className="text-white/30 font-mono-spec text-[11.5px] sm:text-[12px]">{g.items.length} outil{g.items.length > 1 ? 's' : ''}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {g.items.map((it) => <ToolCard key={it.slug} slug={it.slug} allowed={it.allowed} />)}
              </div>
            </div>
          ))}
          {filteredGroups.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-10 text-center text-white/55">
              Aucun outil ne correspond à ton profil pour le moment.
            </div>
          )}
        </div>
        <p className="mt-12 text-white/40 text-[12.5px] max-w-3xl">
          Outils harmonisés autour du Référentiel pour l'Éducation à une Mobilité Citoyenne (REMC). Intégrés à l'identité codevoiture.fr. Accès filtré par profil pour clarté pédagogique.
        </p>
      </section>
    </PageShell>
  );
}
